<template>
    <div class="country-selector">
        <div class="button" @click="handleOpenDialog">
            <img :src="CoWeb" alt="CoWeb" />
            <div class="country-text">{{ countryText }}</div>
            <img
                :src="CoSwitch2"
                alt="CoSwitch2"
                class="switch"
                v-if="isMobile"
            />
        </div>
        <portal to="body">
            <reo-dialog v-if="showSelectedCountry"
                @cancel="showSelectedCountry = false"
                :title="$t('header.select_country')"
                class="chooseDialog"
            >
                <div class="dialog-content">
                    <div v-for="continent in filteredContinentData" :key="continent.code">
                        <h3>{{ continent.name[lang] ?? continent.name.en }}</h3>
                        <ul class="country-list">
                            <li v-for="country in getContinentCountryList(continent.code)" :key="country.countryCode" @click="handleCountryChange(country)">
                                <p class="country">
                                    {{ country.countryText }}
                                </p >
                                <p class=lang> {{ country.langText }}</p>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h3>{{ $t('header.other_regions') }}</h3>
                        <ul class="country-list">
                            <li @click="handleOtherRegion">
                                <p class="country">
                                    {{ $t('header.other_regions') }}
                                </p>
                                <p class=lang> {{ $t('lang.pc.en') }}</p>
                            </li>
                        </ul>
                    </div>
                </div>
            </reo-dialog>
        </portal>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import CoWeb from '@/common/images/common/combo/co_web.svg';
import CoSwitch2 from '@/common/images/common/combo/co_switch2.svg';
import { valueWithFallbacks } from '@/common/js/utils';
import { config } from '@/config/reolink';
import { mapGetters } from 'vuex';
import { ICloudCountry } from '@reolink-cloud/interface';
import ReoDialog from '@/basic/Dialog.vue';
import continentData from 'CONFIG/continentData.json';
import countryData from 'CONFIG/countryData.json';
import { Portal } from 'portal-vue';
import { countryChangeEventBus, BeforeCountryChangeEvent } from '@/view/reolink/checkout/lib/eventBus';
import reolinkGa from '@/common/js/gtagPageview';

export default Vue.extend({
    components: {
        ReoDialog,
        Portal
    },
    created() {

        this.ipCountry = config.countryData.ipCountry;
        this.countryList = config.countryData.countries;

    },
    data () {

        return {

            CoWeb,
            CoSwitch2,
            ipCountry: config.fallbackCountry,
            countryList: [] as ICloudCountry[],
            showSelectedCountry: false,
            continentData,

        };

    },
    computed: {
        ...mapGetters( ['lang', 'isLogin', 'isMobile', 'isStorageAvailableCountry', 'isDataAvailableCountry'] as const ),
        country(): string {

            return valueWithFallbacks( this.$store.getters.country, localStorage.getItem( 'country' ), this.ipCountry );

        },
        countryText(): string {

            if ( this.isCloudNotAvailable ) {

                return this.$t( 'header.other_regions' ) as string;

            }

            return this.countryList.find( country => country.countryCode === this.country )?.countryText ?? this.country;

        },
        filteredContinentData(): typeof continentData {

            return continentData.filter( continent => {

                return this.getContinentCountryList( continent.code ).length > 0;

            });

        },
        isCloudNotAvailable(): boolean {

            return !this.isStorageAvailableCountry && !this.isDataAvailableCountry;

        }
    },
    methods: {
        getContinentCountryList( code: string ) {

            return this.countryList.filter( country => {

                return countryData[country.countryCode] === code;

            });

        },
        handleCountryChange( country: ICloudCountry ) {

            // 发送国家切换前的验证事件
            const event = new BeforeCountryChangeEvent( country.countryCode, country.langCode );
            countryChangeEventBus.dispatchEvent( event );

            // 如果事件被阻止（说明有页面需要处理跳转）
            if ( event.defaultPrevented && event.detail.redirectUrl ) {

                // 先更新 localStorage 和语言
                localStorage.setItem( 'country', country.countryCode );
                this.$store.commit( 'SELECT_LANG', country.langCode );
                this.showSelectedCountry = false;

                // 跳转到指定路径
                location.href = event.detail.redirectUrl;
                return;

            }

            // 正常的国家切换逻辑
            localStorage.setItem( 'country', country.countryCode );
            this.$store.commit( 'SELECT_LANG', country.langCode );
            this.showSelectedCountry = false;
            window.location.reload();

        },
        handleOtherRegion() {

            this.handleCountryChange({
                countryCode: 'other',
                countryText: this.$t( 'header.other_regions' ) as string,
                langCode: 'en',
                langText: this.$t( 'lang.pc.en' ) as string
            });

        },
        handleOpenDialog() {

            this.showSelectedCountry = true;
            reolinkGa.sendSimpleEvent( 'cloud-country' );

        }
    }
});
</script>

<style lang="less" scoped>
.country-selector{
    .mobile({
        padding: 0 24px;
    });
    .button{
        display: flex;
        gap: 4px;
        cursor: pointer;
        .mobile({
            padding: 12px 0;
            border-top: 1px solid @line;
        });
        .country-text{
            font-size: 16px;
            font-weight: 500;
        }
        .switch{
            margin-left: auto;
        }
    }

}

.dialog-content{
    display: flex;
    flex-direction: column;
    gap: 48px;
    width: 872px;
    .mobile({
        width: 100%;
    });
    h3{
        font-size: 24px;
        font-weight: 600;
        line-height: 29px;
        margin-bottom: 12px;
        .mobile({
            font-size: 20px;
        });
    }
    .country-list{
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
        .mobile({
            grid-template-columns: repeat(2, 1fr);
        });
        li{
            padding: 8px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            cursor: pointer;
            &:hover{
                .country{
                    color: @b_6;
                }
            }
            p{
                margin: 0;
                padding: 0;
            }
            .country{
                font-size: 16px;
                font-weight: 400;
                margin-bottom: 2px;
                line-height: 19px;
            }
            .lang{
                font-size: 12px;
                color: @font_secondary;
            }
        }
    }
}
.chooseDialog /deep/ .reo-dialog-inner{
    padding-right: 0 !important;
    .scroll-context{
        padding-right: 32px;
        .mobile({
            padding-right: 16px;
        });
    }
    @media @pc{
        max-width: unset !important;
    }
}
</style>
